import dayjs from 'dayjs'
import type { ComputedRef, Ref } from 'vue'
import type { EventDate } from '~/composables/types/event'
import type { DateRequestFormModel } from '~/composables/useDateRequestFormState'
import { roundToNearest30Minutes } from '~/utils/dateHelpers'

export interface DateRequestDateSlots {
  datePickerStartTime: Ref<{ hours: number; minutes: number }>
  minDateDayjs: ComputedRef<dayjs.Dayjs>
  minDateFormatted: ComputedRef<string>
  minDatePickerStart: ComputedRef<Date>
  getStandardDisabledWeekDays: () => number[]
  isStandardCalendarDateDisabled: (date: Date) => boolean
  isStandardSlotDateAvailable: (
    userDate: string,
    availableDates: readonly EventDate[]
  ) => boolean
  handleNativeDateInput: (
    event: Event,
    form: Ref<DateRequestFormModel>,
    index: number
  ) => void
}

/**
 * Provides date-picker helpers (min date, disabled days, native input
 * normalization, slot availability). Pure helpers that read from
 * `useEventData()`; no form state is owned here.
 */
export const useDateRequestDateSlots = (): DateRequestDateSlots => {
  const eventData = useEventData()

  const datePickerStartTime = ref({ hours: 18, minutes: 0 })

  const minDateDayjs = computed(() =>
    dayjs().add(
      eventGetters.getDateRequestMinAdvanceQuantity(eventData.value),
      eventGetters
        .getDateRequestMinAdvanceUnit(eventData.value)
        .toLowerCase() as dayjs.ManipulateType
    )
  )
  const minDateFormatted = computed(() =>
    minDateDayjs.value.format('YYYY-MM-DD')
  )
  const minDatePickerStart = computed(() => minDateDayjs.value.toDate())

  const getStandardDisabledWeekDays = () =>
    [0, 1, 2, 3, 4, 5, 6].filter(
      (day) =>
        !eventGetters.getDateRequestAvailableDays(eventData.value).includes(day)
    )

  const isStandardCalendarDateDisabled = (date: Date) =>
    dayjs(date).isBefore(minDateDayjs.value.startOf('day'))

  const isStandardSlotDateAvailable = (
    userDate: string,
    availableDates: readonly EventDate[]
  ) => {
    if (!userDate || !availableDates || availableDates.length === 0) {
      return false
    }
    return availableDates.some((availableDate) => {
      const userDateObj = dayjs(userDate)
      const availableDateObj = dayjs(availableDate.start)
      if (!userDateObj.isSame(availableDateObj, 'day')) {
        return false
      }
      const timeDiffMinutes = Math.abs(
        userDateObj.diff(availableDateObj, 'minutes')
      )
      return timeDiffMinutes <= 60
    })
  }

  const handleNativeDateInput = (
    event: Event,
    form: Ref<DateRequestFormModel>,
    index: number
  ) => {
    const target = event.target as HTMLInputElement
    const inputValue = target?.value
    if (!inputValue) {
      return
    }
    const formatted = roundToNearest30Minutes(inputValue)
    if (formatted && form?.value?.dates) {
      form.value.dates[index] = formatted
      target.value = formatted
    }
  }

  return {
    datePickerStartTime,
    minDateDayjs,
    minDateFormatted,
    minDatePickerStart,
    getStandardDisabledWeekDays,
    isStandardCalendarDateDisabled,
    isStandardSlotDateAvailable,
    handleNativeDateInput,
  }
}
